import { isApiError, isTransientError } from '@/lib/errors';
import { logger } from '@/lib/logger';

// Poll cadence for a dispatched import job. 1.5s is responsive enough that a
// small org's import still feels immediate, without hammering the endpoint
// through the minutes a real org's import can take.
const JOB_POLL_INTERVAL_MS = 1500;
const JOB_POLL_TIMEOUT_MS = 35 * 60 * 1000;
// Transient network errors (5xx, dropped connections) retry up to this many
// times before the error propagates — avoids killing a long-running import on
// a brief connectivity hiccup.
const MAX_NETWORK_RETRIES = 3;

export type VendorJobStatus = 'pending' | 'running' | 'succeeded' | 'failed';

export interface VendorJob<TErrorCode extends string = string> {
  id: number;
  status: VendorJobStatus;
  result: Record<string, unknown> | null;
  error_code: TErrorCode | '';
  error_detail: Record<string, unknown> | null;
  csv_import_id: number | null;
  created_at: string;
  finished_at: string | null;
}

/** Thrown when a dispatched job reaches a terminal `failed` state. */
export class VendorJobFailedError extends Error {
  readonly code: string;
  readonly detail: Record<string, unknown> | null;

  constructor(job: {
    id: number;
    error_code: string;
    error_detail: Record<string, unknown> | null;
    [key: string]: unknown;
  }) {
    super(`Import job ${job.id} failed: ${job.error_code || 'unknown'}`);
    this.name = 'VendorJobFailedError';
    this.code = job.error_code;
    this.detail = job.error_detail;
  }
}

/** Thrown when polling gives up. The job itself may still be running. */
export class VendorJobTimeoutError extends Error {
  constructor() {
    super('Timed out waiting for the import job');
    this.name = 'VendorJobTimeoutError';
  }
}

export interface WaitForVendorJobOptions {
  signal?: AbortSignal;
  onStatus?: (status: VendorJobStatus) => void;
  timeoutMs?: number;
  pollIntervalMs?: number;
  maxNetworkRetries?: number;
  /**
   * Restores the pre-refactor "retry any non-fatal error" polling semantics
   * for vendors that need byte-identical backward compatibility with their
   * pre-refactor polling behavior. When true, the `isTransientError` gate is
   * bypassed and any error (e.g. a 400/403/404 `ApiError`) is retried up to
   * `maxNetworkRetries` times, not just transient ones. New vendors should
   * leave this unset to get the smarter transient-only default. Defaults to
   * `false`.
   */
  retryAllErrors?: boolean;
}

/**
 * Poll a dispatched job until it reaches a terminal state.
 *
 * The ceiling is deliberately longer than the backend tasks' own hard time
 * limits so this never gives up on a job that is still legitimately working —
 * the whole point of moving this work off the request path was that a real
 * org's import does not fit in a single request. A timeout here means "we
 * stopped watching", not "it failed", and the copy should say so.
 *
 * Accepts an optional AbortSignal to cancel polling (e.g. on wizard unmount).
 * Transient network errors are retried up to `maxNetworkRetries` times before
 * propagating — a brief connectivity hiccup will not kill a long-running
 * import.
 *
 * `onStatus` is invoked on each *change* of the job's non-terminal status. The
 * distinction matters to the user and is otherwise discarded here: `pending`
 * means the job is queued and no worker has claimed it yet, `running` means it
 * is actually doing the work. Collapsing both into "still waiting" is what
 * made a five-minute queue wait indistinguishable from a hung fetch.
 */
export async function waitForVendorJob<TJob extends VendorJob>(
  getJob: (signal?: AbortSignal) => Promise<TJob>,
  options: WaitForVendorJobOptions = {}
): Promise<TJob> {
  const {
    signal,
    onStatus,
    timeoutMs = JOB_POLL_TIMEOUT_MS,
    pollIntervalMs = JOB_POLL_INTERVAL_MS,
    maxNetworkRetries = MAX_NETWORK_RETRIES,
    retryAllErrors = false,
  } = options;
  const deadline = Date.now() + timeoutMs;
  let networkErrorCount = 0;
  let lastStatus: VendorJobStatus | null = null;
  for (;;) {
    if (signal?.aborted) {
      throw new DOMException('Polling aborted', 'AbortError');
    }
    // Check the deadline before every request. A pollIntervalMs longer than the
    // remaining budget must not let the loop sleep past the deadline and then
    // issue another request whose `succeeded` response would return success
    // *after* the configured timeout.
    if (Date.now() >= deadline) {
      throw new VendorJobTimeoutError();
    }
    let job: TJob;
    try {
      job = await getJob(signal);
      networkErrorCount = 0;
    } catch (err) {
      if (err instanceof VendorJobFailedError) {
        throw err;
      }
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      // Only transient failures (5xx, dropped connections, timeouts) are worth
      // retrying. A non-transient status like 400/404 — a missing or
      // unauthorized job — is a definitive answer; retrying it just burns
      // maxNetworkRetries * pollIntervalMs (~4.5s) of redundant polling before
      // surfacing the same error to the user. `retryAllErrors` opts a vendor
      // out of this gate entirely, retrying any non-fatal error up to
      // `maxNetworkRetries` — see the option's JSDoc for why this exists.
      const isNonTransient = !isTransientError(err);
      if (
        (!retryAllErrors && isNonTransient) ||
        networkErrorCount >= maxNetworkRetries
      ) {
        // The only way to reach this branch with a *transient* error is
        // exhausted retries (a non-transient error either fails immediately or,
        // under retryAllErrors, logs and re-throws below). A transient failure
        // that only ran out of retries at/after the polling deadline is really
        // "we stopped watching", not a hard error — surface the same
        // VendorJobTimeoutError the success path throws rather than the raw
        // network error. A non-transient error (400/404) is a definitive answer
        // and still propagates raw regardless of the deadline.
        if (!isNonTransient && Date.now() >= deadline) {
          throw new VendorJobTimeoutError();
        }
        if (retryAllErrors && isNonTransient) {
          logger.warn(
            'waitForVendorJob: exhausted retries on non-transient error',
            {
              networkErrorCount,
              maxNetworkRetries,
              statusCode: isApiError(err) ? err.statusCode : undefined,
              errorCode: isApiError(err) ? err.code : undefined,
              error: err,
            }
          );
        }
        throw err;
      }
      networkErrorCount++;
      if (signal?.aborted) {
        throw new DOMException('Polling aborted', 'AbortError');
      }
      // Honor the polling ceiling even when the latest attempt failed
      // transiently. Without this, a hiccup at/after the deadline would keep
      // retrying and ultimately surface the raw network error instead of the
      // "still running" timeout — the same contract the success path enforces
      // below. A non-transient error (only reachable here under retryAllErrors,
      // with retries unexhausted) is a definitive answer and must propagate raw
      // even at the deadline — only transient failures convert to a timeout.
      if (Date.now() >= deadline) {
        if (isNonTransient) {
          throw err;
        }
        throw new VendorJobTimeoutError();
      }
      // Cap the sleep to the remaining budget so a pollIntervalMs longer than
      // what is left cannot carry the loop past the deadline.
      const retrySleepMs = Math.min(
        pollIntervalMs,
        Math.max(0, deadline - Date.now())
      );
      await new Promise(resolve => setTimeout(resolve, retrySleepMs));
      continue;
    }
    if (job.status === 'succeeded') {
      return job;
    }
    if (job.status === 'failed') {
      throw new VendorJobFailedError({
        id: job.id,
        error_code: job.error_code,
        error_detail: job.error_detail,
      });
    }
    if (job.status !== lastStatus) {
      lastStatus = job.status;
      onStatus?.(job.status);
    }
    if (Date.now() >= deadline) {
      throw new VendorJobTimeoutError();
    }
    if (signal?.aborted) {
      throw new DOMException('Polling aborted', 'AbortError');
    }
    // Cap the sleep to the remaining budget; the top-of-loop deadline check then
    // ends polling before another request rather than overshooting the timeout.
    const sleepMs = Math.min(
      pollIntervalMs,
      Math.max(0, deadline - Date.now())
    );
    await new Promise(resolve => setTimeout(resolve, sleepMs));
  }
}
